/**
 * Sincroniza entrenamientos pendientes (guardados offline) con Firestore.
 * Se ejecuta globalmente al cargar la app y al reconectar.
 */
import { addDoc, collection, serverTimestamp, doc, updateDoc, query, where, getDocs, limit } from "firebase/firestore";
import { db } from "../firebase";
import { formatLocalDateString, utcDateString } from "./workoutDates";

const CACHE_KEY_PREFIX = "workout_cache_";
const CACHE_EXPIRY_HOURS = 24;

// Lock global: evita que App y RoutineDayView sincronicen el mismo cache key a la vez (worklogs duplicados)
const syncingKeys = new Set();

function parseRepsValue(rawReps) {
  if (typeof rawReps === "number" && Number.isFinite(rawReps) && rawReps > 0) {
    return rawReps;
  }
  if (typeof rawReps !== "string") return 0;
  const matches = rawReps.match(/\d+/g);
  if (!matches || matches.length === 0) return 0;
  const nums = matches.map((m) => Number(m)).filter((n) => Number.isFinite(n) && n > 0);
  if (nums.length === 0) return 0;
  if (nums.length === 1) return nums[0];
  return Math.round(nums.reduce((a, b) => a + b, 0) / nums.length);
}

function loadCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    const hoursElapsed = (Date.now() - timestamp) / (1000 * 60 * 60);
    if (hoursElapsed > CACHE_EXPIRY_HOURS) {
      localStorage.removeItem(key);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function saveCache(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now(), version: "2.0" }));
  } catch (e) {
    console.warn("Error guardando cache:", e);
  }
}

function clearCache(key) {
  try {
    localStorage.removeItem(key);
  } catch {}
}

/**
 * Sincroniza un entrenamiento pendiente a Firestore.
 * @returns {Promise<string|false>} id del worklog si se sincronizó, false si no había nada o ya estaba sincronizando
 */
export async function syncPendingWorkout(cacheKey, userId, onSynced) {
  if (!navigator.onLine) return false;
  if (syncingKeys.has(cacheKey)) return false;
  syncingKeys.add(cacheKey);

  try {
    const cached = loadCache(cacheKey);
    if (!cached) return false;
    const hasExistingId = !!cached.workoutSessionId;
    const isCompleted = cached.workoutState === "finished" || cached.workoutState === "completed";
    if (hasExistingId && !isCompleted) return false; // ya sincronizado (paused/active), nada que hacer
    if (cached.syncingAt && (Date.now() - cached.syncingAt) < 90000) return false;

    const exercises = cached.dayData?.exercises;
  if (!exercises?.length) return false;

  const routineId = cached.routineId;
  const routineName = cached.routineName || "Rutina";
  const day = cached.dayData?.name || cacheKey.split("_").pop() || "Día";

  saveCache(cacheKey, { ...cached, syncingAt: Date.now() });

  const completedSet = new Set(cached.completedExercises || []);
  const prevWeights = cached.previousWeights || {};
  const status = (cached.workoutState === "finished" || cached.workoutState === "completed") ? "completed" : cached.workoutState === "paused" ? "paused" : "active";
  const shouldInheritWeights = status === "completed";

  const exerciseData = exercises.map((exercise, index) => {
    const isCompleted = completedSet.has(index) || exercise?.completed === true;
    const sets = Number(exercise.sets);
    const reps = parseRepsValue(exercise.reps);
    const safeSets = !Number.isNaN(sets) && sets > 0 ? sets : 0;
    const safeReps = !Number.isNaN(reps) && reps > 0 ? reps : 0;
    const rawTodayWeights = Array.isArray(exercise.pesoRealizado) ? exercise.pesoRealizado : [];
    const prev = Array.isArray(prevWeights[index]) ? prevWeights[index] : [];
    // Heredar pesos faltantes por serie desde la sesión previa, pero
    // limitando estrictamente al número de series actual del ejercicio.
    const mergeLen = safeSets > 0 ? safeSets : 0;
    const mergedWeights = Array.from({ length: mergeLen }, (_, i) => {
      const w = rawTodayWeights[i];
      const missingToday = w === undefined || w === null || w === "";
      if (!missingToday) return w;
      // Heredar solo al finalizar y solo para ejercicios realmente completados.
      return (shouldInheritWeights && isCompleted) ? (prev[i] ?? 0) : 0;
    });

    const normalizedWeights = mergedWeights.map((w) => {
      const num = Number(w);
      // 0 se guarda como 0 real; solo invalidamos NaN/negativos
      return Number.isFinite(num) && num >= 0 ? num : 0;
    });
    // "Realizadas" = series cargadas manualmente en esta sesión.
    // Si marcó el ejercicio como completado, se consideran todas las series del ejercicio.
    const typedSets = rawTodayWeights.filter((w) => w !== undefined && w !== null && w !== "").length;
    const validWeights = normalizedWeights.filter((w) => w > 0);
    const completedSetsRaw = validWeights.length > 0 ? validWeights.length : (isCompleted ? safeSets : 0);
    const completedSets = safeSets > 0 ? Math.min(completedSetsRaw, safeSets) : 0;
    const performedSetsRaw = isCompleted ? safeSets : typedSets;
    return {
      name: exercise.name || "",
      sets: safeSets,
      reps: safeReps,
      description: exercise.description || "",
      completed: isCompleted,
      completedSets,
      weights: normalizedWeights,
      maxWeight: validWeights.length > 0 ? Math.max(...validWeights) : 0,
      // Volumen total del ejercicio: sumatoria de (peso por serie * reps del ejercicio)
      totalWeight: validWeights.reduce((sum, w) => sum + (w * safeReps), 0),
      planned: true
    };
  });

  const cachedStart = cached.workoutStartTime ? new Date(cached.workoutStartTime) : null;
  const hasCachedStart = cachedStart instanceof Date && !Number.isNaN(cachedStart.getTime());
  const startTime = hasCachedStart ? cachedStart : new Date();
  const totalPaused = cached.totalPausedDuration || 0;
  const endTime = status === "completed" && cached.workoutEndTime ? new Date(cached.workoutEndTime) : null;
  const currentDate = new Date();
  const trainingDateString = formatLocalDateString(startTime);
  const legacyUtcDateString = utcDateString(startTime);
  const finalDuration = endTime
    ? Math.floor(((endTime - startTime) - totalPaused) / 60000)
    : Math.floor(((currentDate - startTime) - totalPaused) / 60000);

  const completedCount = exerciseData.filter((ex) => ex.completed).length;
  // totalRepetitions = reps planificadas en rutina (sets * reps)
  const totalRepetitions = exerciseData.reduce((sum, ex) => {
    return sum + ((ex.sets || 0) * (ex.reps || 0));
  }, 0);
  // totalPerformedRepetitions = reps realmente realizadas en la sesión
  const totalPerformedRepetitions = exerciseData.reduce((sum, ex, idx) => {
    const safeSets = Number(ex.sets) || 0;
    const reps = Number(ex.reps) || 0;
    const isCompleted = completedSet.has(idx) || ex.completed === true;
    return sum + (isCompleted ? (safeSets * reps) : 0);
  }, 0);
  const workoutData = {
    routineId,
    routineName,
    dayName: day,
    exercises: exerciseData,
    startTime,
    endTime,
    status,
    completedExercises: completedCount,
    completedSeries: exerciseData.reduce((sum, ex) => sum + (ex.completedSets || 0), 0),
    totalRepetitions,
    totalPerformedRepetitions,
    totalWeight: exerciseData.reduce((sum, ex) => sum + (ex.completed ? (ex.totalWeight || 0) : 0), 0),
    plannedExercises: exerciseData.length,
    plannedSeries: exerciseData.reduce((sum, ex) => sum + (ex.sets || 0), 0),
    duration: Math.max(0, finalDuration),
    totalPausedDuration: totalPaused,
    lastModified: currentDate,
    dateString: trainingDateString,
    date: startTime,
    completionRate: exerciseData.length > 0 ? (completedCount / exerciseData.length) * 100 : 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...(status === "completed" && completedCount < exerciseData.length ? { incomplete: true } : {})
  };

    try {
      let newId;
      const { createdAt, ...updatePayload } = workoutData;
      if (!hasCachedStart) {
        delete updatePayload.startTime;
        delete updatePayload.date;
        delete updatePayload.dateString;
      }

      if (hasExistingId && isCompleted) {
        const workoutRef = doc(db, "users", userId, "workoutLogs", cached.workoutSessionId);
        await updateDoc(workoutRef, { ...updatePayload, updatedAt: serverTimestamp() });
        newId = cached.workoutSessionId;
      } else {
        const colRef = collection(db, "users", userId, "workoutLogs");
        const dateKeys = Array.from(new Set([trainingDateString, legacyUtcDateString].filter(Boolean)));
        let existingDoc = null;
        for (const dateKey of dateKeys) {
          const existingSnap = await getDocs(query(
            colRef,
            where("routineId", "==", routineId),
            where("dayName", "==", day),
            where("dateString", "==", dateKey),
            limit(1)
          ));
          if (existingSnap.empty) continue;
          const candidate = existingSnap.docs[0];
          if (dateKey === trainingDateString) {
            existingDoc = candidate;
            break;
          }
          const candidateData = candidate.data();
          const candidateDay = formatLocalDateString(
            candidateData.startTime || candidateData.date || candidateData.dateString
          );
          if (!candidateDay || candidateDay === trainingDateString) {
            existingDoc = candidate;
            break;
          }
        }

        if (existingDoc) {
          const workoutRef = doc(db, "users", userId, "workoutLogs", existingDoc.id);
          await updateDoc(workoutRef, { ...updatePayload, updatedAt: serverTimestamp() });
          newId = existingDoc.id;
        } else {
          const workoutRef = await addDoc(colRef, workoutData);
          newId = workoutRef.id;
        }
        saveCache(cacheKey, { ...cached, workoutSessionId: newId, lastUpdated: Date.now() });
      }
      if (status === "completed") clearCache(cacheKey);
      if (typeof onSynced === "function") onSynced();
      return newId;
    } catch (err) {
      console.error("Error sincronizando entrenamiento pendiente:", err);
      const { syncingAt, ...rest } = cached;
      saveCache(cacheKey, rest);
      return false;
    }
  } finally {
    syncingKeys.delete(cacheKey);
  }
}

/**
 * Sincroniza todos los entrenamientos pendientes del usuario.
 * Ejecutar al cargar la app y al evento 'online'.
 * @param {string} userId
 * @param {(syncedCount: number) => void} [onSynced] - Se llama una vez al final con el número de entrenamientos sincronizados
 */
export async function syncAllPendingWorkouts(userId, onSynced) {
  if (!userId || !navigator.onLine) return;
  const prefix = `${CACHE_KEY_PREFIX}${userId}_`;
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) keys.push(key);
  }
  let syncedCount = 0;
  for (const key of keys) {
    const ok = await syncPendingWorkout(key, userId);
    if (ok) syncedCount++;
  }
  if (syncedCount > 0 && typeof onSynced === "function") onSynced(syncedCount);
}
